<script setup lang="ts">
import { NuxtUiIcons } from '@shared/types'
import { IS_ORDERBOOK_ENABLED } from '@/app/utils/constants'
import { isDerivativeGeoRestricted } from '@/app/data/geoip'
import type { SelectMenuItem } from '@nuxt/ui'

const route = useRoute()
const appStore = useAppStore()
const sharedGeoStore = useSharedGeoStore()
const { $onError } = useNuxtApp()
const { t, locales, setLocale, localeCodes } = useLang()

const languageItems = computed<SelectMenuItem[]>(() =>
  locales.value.map((locale) => ({
    label: locale.longName as string,
    value: locale.code
  }))
)

const selectedLanguage = computed({
  get: () =>
    appStore.userState.preferences.selectedLanguage ||
    localeCodes.value[0] ||
    'en',
  set: (value: string) => onUpdateLocale(value)
})

const isOrderbookVisible = computed({
  get: () => appStore.userState.preferences.isOrderbookVisible,
  set: () => appStore.toggleOrderbookVisibility()
})

const isOrderFillSoundEnabled = computed({
  get: () => appStore.userState.preferences.playOrderFillSound,
  set: () => appStore.toggleOrderFillSound()
})

const isRfqFastExecutionEnabled = computed({
  get: () => appStore.userState.preferences.isRfqFastExecutionEnabled,
  set: () => appStore.toggleRfqGatewayPrefetch()
})

const isRfqClientGatewayEnabled = computed({
  get: () => appStore.userState.preferences.isRfqClientGatewayEnabled,
  set: () => appStore.toggleRfqClientGateway()
})

const isAutoMarketCloseEnabled = computed({
  get: () => appStore.userState.preferences.isAutoMarketCloseEnabled,
  set: () => appStore.toggleAutoMarketClose()
})

const rfqGatewayPrefetchCheckboxProps = computed(() => ({
  label: t('common.fastRfqExecution'),
  ui: {
    wrapper: 'me-2 ms-0'
  }
}))

const rfqClientGatewayCheckboxProps = computed(() => ({
  label: t('common.rfqClientGateway'),
  ui: {
    wrapper: 'me-2 ms-0'
  }
}))

function onUpdateLocale(selectedLocale: string) {
  setLocale(selectedLocale as (typeof localeCodes.value)[number])

  appStore.setSelectedLanguage(selectedLocale)
  import('@/app/providers/mixpanel/EventTracker')
    .then(({ trackChangeSelectedLanguage }) => {
      trackChangeSelectedLanguage(selectedLocale)
    })
    .catch($onError)
}

function setAppLanguage() {
  const userDefaultLanguage = navigator?.language?.split('-')[0]

  const defaultLanguage =
    localeCodes.value.find((lang) => lang === userDefaultLanguage) ||
    localeCodes.value[0] ||
    'en'

  let language = localeCodes.value.find(
    (lang) => lang === appStore.userState.preferences.selectedLanguage
  )

  const prefixLanguage = localeCodes.value.find(
    (lang) => lang === route.query?.lang
  )

  if (prefixLanguage) {
    language = prefixLanguage
  }

  if (language) {
    setLocale(language)
    appStore.setSelectedLanguage(language)
    trackInitialSelectedLanguage(language)
  } else {
    setLocale(defaultLanguage)
    appStore.setSelectedLanguage(defaultLanguage)
    trackInitialSelectedLanguage(defaultLanguage)
  }
}

function trackInitialSelectedLanguage(selectedLocale: string) {
  import('@/app/providers/mixpanel/EventTracker')
    .then(({ trackInitialSelectedLanguage }) => {
      trackInitialSelectedLanguage(selectedLocale)
    })
    .catch($onError)
}

onMounted(() => {
  setAppLanguage()
})
</script>

<template>
  <UPopover
    modal
    :content="{ side: 'bottom', align: 'end', sideOffset: 12 }"
    :ui="{ content: 'p-0 max-w-48' }"
  >
    <UButton
      class="bg-transparent text-toned p-1.5"
      v-bind="{
        size: 'xs',
        color: 'neutral',
        variant: 'outline',
        icon: NuxtUiIcons.SettingsOutline,
        ui: {
          leadingIcon: 'size-4 min-w-4 lg:size-3 lg:min-w-3'
        }
      }"
    />

    <template #content>
      <h3 class="px-3 py-2.5 uppercase text-xs font-semibold text-muted">
        {{ $t('common.settings') }}
      </h3>

      <USeparator />

      <div class="p-3 space-y-4">
        <div class="flex items-center justify-between gap-3">
          <span class="text-xs font-medium">{{ $t('common.language') }}</span>

          <USelectMenu
            v-model="selectedLanguage"
            value-key="value"
            class="w-32"
            :search-input="false"
            :items="languageItems"
            v-bind="{
              size: 'xs',
              color: 'neutral',
              variant: 'subtle'
            }"
          />
        </div>

        <UCheckbox
          v-if="IS_ORDERBOOK_ENABLED"
          v-model="isOrderbookVisible"
          class="flex-row-reverse"
          :label="$t('common.showOrderbook')"
          :ui="{
            wrapper: 'me-2 ms-0'
          }"
        />

        <UCheckbox
          v-if="!isDerivativeGeoRestricted(sharedGeoStore.geoCountry)"
          v-model="isRfqFastExecutionEnabled"
          v-bind="rfqGatewayPrefetchCheckboxProps"
          class="flex-row-reverse"
        />

        <UCheckbox
          v-if="!isDerivativeGeoRestricted(sharedGeoStore.geoCountry)"
          v-model="isRfqClientGatewayEnabled"
          v-bind="rfqClientGatewayCheckboxProps"
          class="flex-row-reverse"
        />

        <UCheckbox
          v-model="isAutoMarketCloseEnabled"
          class="flex-row-reverse"
          :ui="{
            wrapper: 'me-2 ms-0'
          }"
        >
          <template #label>
            <AppTooltip
              has-arrow
              :content="{ side: 'left', align: 'center' }"
            >
              <span>{{ $t('common.autoMarketClose') }}</span>

              <template #tooltip>
                {{ $t('common.autoMarketCloseTooltip') }}
              </template>
            </AppTooltip>
          </template>
        </UCheckbox>

        <UCheckbox
          v-model="isOrderFillSoundEnabled"
          class="flex-row-reverse"
          :label="$t('common.playOrderFillSound')"
          :ui="{
            wrapper: 'me-2 ms-0'
          }"
        />
      </div>
    </template>
  </UPopover>
</template>
